// src/analytics/gtag.js
// Minimal gtag wrapper for GA4 (+ optional Google Ads) with cross-domain support.

// Helper to check if we're on production domain
function isProduction() {
  return process.env.REACT_APP_DOMAIN === "permitrockstar.app";
}

export function loadGtag({
  ga4Id = process.env.REACT_APP_GA4_MARKETING,
  adsId = null,
  crossDomains = ["permitrockstar.com", "permitrockstar.app"],
  sendPageViewAutomatically = false, // SPA => false
} = {}) {
  if (typeof window === "undefined") return;
  if (window.gtag && window.__gtagLoaded) return;

  // Create global gtag & dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  // Load gtag.js using the first ID (GA4 or Ads)
  const firstId = ga4Id || adsId || "";
  if (!firstId) {
    return;
  }
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(firstId)}`;
  document.head.appendChild(s);

  window.gtag("js", new Date());

  // GA4 configs (with cross-domain linker + no auto pageview)
  if (ga4Id) {
    window.gtag("config", ga4Id, {
      send_page_view: sendPageViewAutomatically,
      linker: { domains: crossDomains },
    });
  }

  // Google Ads config (if provided)
  if (adsId) window.gtag("config", adsId);

  window.__gtagLoaded = true;
}

export function trackPageView(path) {
  if (!isProduction() || !window.gtag) return;
  // GA4: manual page_view for SPA
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function trackSignUp({ method = "email", plan = "free" } = {}) {
  if (!isProduction() || !window.gtag) return;
  // GA4 recommended event; mark 'sign_up' as a Conversion in GA4 UI
  window.gtag("event", "sign_up", {
    method,
    plan,
    reg_session_id: getOrMakeSessionId(),
  });
}

export function trackAdsRegistration({
  adsId,
  conversionLabel,
  value = 0,
  currency = "USD",
} = {}) {
  if (!isProduction() || !window.gtag || !adsId || !conversionLabel) return;
  // Direct Google Ads conversion (optional)
  window.gtag("event", "conversion", {
    send_to: `${adsId}/${conversionLabel}`,
    value,
    currency,
  });
}

export function setUserId(userId, ga4Id = process.env.REACT_APP_GA4_MARKETING) {
  if (!isProduction() || !window.gtag || !userId || !ga4Id) return;
  window.gtag("config", ga4Id, { user_id: String(userId) });
}

export function getOrMakeSessionId() {
  try {
    const k = "pr_reg_session_id";
    let v = sessionStorage.getItem(k);
    if (!v) {
      v = crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
      sessionStorage.setItem(k, v);
    }
    return v;
  } catch {
    return undefined;
  }
}

export function trackRegistrationStart({ method = "email" } = {}) {
  if (!isProduction() || !window.gtag) return;
  window.gtag("event", "registration_start", {
    method,
    reg_session_id: getOrMakeSessionId(),
  });
}

export function trackRegistrationStep({ step, method = "email" } = {}) {
  if (!isProduction() || !window.gtag || !step) return;
  window.gtag("event", "registration_step", {
    step, // e.g. "email", "otp", "profile", "plan"
    method,
    reg_session_id: getOrMakeSessionId(),
  });
}

export function trackRegistrationSubmit({
  method = "email",
  extras = {},
} = {}) {
  if (!isProduction() || !window.gtag) return;
  window.gtag("event", "registration_submit", {
    method,
    reg_session_id: getOrMakeSessionId(),
    ...extras, // e.g. plan, admin, has_promo
  });
}

export function trackRegistrationError({ code, fields = [] } = {}) {
  if (!isProduction() || !window.gtag) return;
  window.gtag("event", "registration_error", {
    code, // e.g. "validation_failed" | "license_mismatch" | "email_exists"
    fields: fields.join(","), // optional: "email,phone"
    reg_session_id: getOrMakeSessionId(),
  });
}
